import {
  useRef,
  useState
} from 'react'
import {
  Camera,
  Loader2,
  RefreshCw,
  User
} from 'lucide-react'
import {
  authService,
  getApiErrorMessage
} from '../services/api'
import type {
  AssistantCharacter
} from '../services/api'
import type {
  LucideIcon
} from 'lucide-react'

// Mirrors the backend's own limits (routers/auth.py). Checking here too is
// only to fail fast on the client — the endpoint re-validates, since the
// control being hidden is not what stops an upload.
const MAX_BYTES = 8 * 1024 * 1024
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
const ACCEPT_ATTR = ACCEPTED_TYPES.join(',')

const BUTTON_TONES = {
  primary: 'text-primary-700 border-primary-200 hover:bg-primary-50',
  muted: 'text-gray-600 border-gray-200 hover:bg-gray-50',
} as const

function PictureButton({
  icon: Icon,
  spinning,
  disabled,
  onClick,
  tone,
  children,
}: {
  icon: LucideIcon
  spinning: boolean
  disabled: boolean
  onClick: () => void
  tone: keyof typeof BUTTON_TONES
  children: React.ReactNode
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-white border rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${BUTTON_TONES[tone]}`}
    >
      {spinning ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <Icon className="w-3.5 h-3.5" />
      )}
      {children}
    </button>
  )
}

interface AssistantPictureFieldProps {
  character: AssistantCharacter
  /** Called with the updated character after an upload or a revert. */
  onChanged: (character: AssistantCharacter) => void
  disabled?: boolean
}

/**
 * The assistant's profile picture, with a photo upload beside the generated
 * headshot (GET-8327). Rendered only for the user's own custom character and
 * only while the `custom-profile-picture-upload` flag is on — built-in
 * characters are shared rows and can never carry a per-user photo.
 */
export function AssistantPictureField({
  character,
  onChanged,
  disabled = false,
}: AssistantPictureFieldProps): JSX.Element {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState<'upload' | 'revert' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleFileSelected = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = e.target.files?.[0]
    // Let the same file be picked again after a failure — the input keeps its
    // value otherwise and the change event never fires a second time.
    e.target.value = ''
    if (!file) return

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Pick a JPEG, PNG, WebP or GIF image.')
      return
    }
    if (file.size > MAX_BYTES) {
      setError('Pick an image under 8 MB.')
      return
    }

    setBusy('upload')
    setError(null)
    try {
      onChanged(await authService.uploadCustomCharacterPicture(file))
    } catch (err) {
      console.error('Failed to upload assistant picture:', err)
      setError(
        getApiErrorMessage(err, "We couldn't save that picture. Please try again."),
      )
    } finally {
      setBusy(null)
    }
  }

  const handleRevert = async (): Promise<void> => {
    setBusy('revert')
    setError(null)
    try {
      onChanged(await authService.revertCustomCharacterPicture())
    } catch (err) {
      console.error('Failed to revert assistant picture:', err)
      setError(
        getApiErrorMessage(err, "We couldn't draw a new picture. Please try again."),
      )
    } finally {
      setBusy(null)
    }
  }

  const locked = disabled || busy !== null

  return (
    <div>
      <span className="block text-sm font-medium text-gray-700 mb-1.5">
        Picture
      </span>
      <div className="flex items-center gap-4">
        {character.picture_url ? (
          <img
            src={character.picture_url}
            alt={character.name}
            className="w-16 h-16 rounded-full object-cover ring-2 ring-white shadow-md shrink-0"
          />
        ) : (
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center ring-2 ring-white shadow-md shrink-0">
            {busy === 'revert' ? (
              <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
            ) : (
              <User className="w-6 h-6 text-gray-400" />
            )}
          </div>
        )}
        <div className="space-y-1">
          <div className="flex flex-wrap gap-2">
            <PictureButton
              icon={Camera}
              spinning={busy === 'upload'}
              disabled={locked}
              onClick={() => fileInputRef.current?.click()}
              tone="primary"
            >
              {character.picture_is_uploaded ? 'Change photo' : 'Upload a photo'}
            </PictureButton>
            {character.picture_is_uploaded && (
              <PictureButton
                icon={RefreshCw}
                spinning={busy === 'revert'}
                disabled={locked}
                onClick={() => void handleRevert()}
                tone="muted"
              >
                Use a generated picture
              </PictureButton>
            )}
          </div>
          <p className="text-xs text-gray-400">
            {busy === 'revert'
              ? 'Drawing a new picture — this takes about a minute.'
              : 'JPEG, PNG, WebP or GIF, up to 8 MB. Square works best.'}
          </p>
        </div>
      </div>
      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_ATTR}
        onChange={(e) => void handleFileSelected(e)}
        className="hidden"
      />
    </div>
  )
}
